/**
 * PageHeader — der Bereichskopf (Baukasten): Wald-Fläche mit Pfad oben links,
 * Bereichsumschalter oben rechts, Titel und den Reitern der Seite. Pfad, Umschalter und Reiter
 * kommen aus dem Gerüst (`useAreaHeader`); die Seite liefert Titel, Beschreibung und Aktionen.
 * Ohne Gerüst (Bühnen) erscheint stattdessen die Eyebrow.
 */
import type { ReactNode } from 'react';
import {
  AreaCrumbs,
  AreaSwitcher,
  AreaTabs,
  useAreaHeader,
  useRegisterAreaHeader,
} from '@/shared/navigation';
import { cn } from '@/shared/lib/cn';

export interface PageHeaderProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  /** Wort am Ende des Titels, in Minze abgesetzt. */
  titleAccent?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Werkzeuge direkt auf dem Wald (Zeitraum, Heute, Länge) — ohne die weiße Aktionsfläche. */
  tools?: ReactNode;
  /** Eine Werkzeugzeile unter dem Titel, etwa Dichte und „Filter und Ansicht“ am Handy. */
  below?: ReactNode;
  /** Großes Element auf dem Wald zwischen Titel und Reitern, etwa eine Kurve. */
  feature?: ReactNode;
  /** Reiter der Seite selbst, z. B. ein Statusfilter; ersetzen die Reiter aus dem Gerüst. */
  /** `count`: Zahl aus vollständig geladenen Serverdaten, als Plakette neben dem Wort. */
  filterTabs?: { label: string; active: boolean; onSelect: () => void; count?: number }[];
  filterTabsLabel?: string;
  /** `tabs`: echte Reiter (Inhalt wechselt); sonst Filterknöpfe über einer Liste. */
  filterTabsKind?: 'filter' | 'tabs';
  className?: string;
}

export function PageHeader({
  eyebrow,
  title,
  titleAccent,
  description,
  actions,
  tools,
  below,
  feature,
  filterTabs,
  filterTabsLabel = 'Filter',
  filterTabsKind = 'filter',
  className,
}: PageHeaderProps) {
  useRegisterAreaHeader();
  const area = useAreaHeader();
  const hasTop = area.crumbs.length > 0 || area.switcher.length > 1;
  return (
    <header
      className={cn(
        'on-forest bg-forest-deep rounded-hero relative mb-6 flex-none overflow-hidden px-5 py-5 text-white md:px-8 md:py-7',
        className,
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(115deg,rgba(255,255,255,0.035)_0_2px,transparent_2px_24px)]"
      />
      {hasTop && (
        <div className="relative mb-4 flex flex-wrap items-center justify-between gap-3">
          <AreaCrumbs crumbs={area.crumbs} />
          <AreaSwitcher items={area.switcher} />
        </div>
      )}
      <div className="relative flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          {!hasTop && eyebrow && (
            <p className="text-mint mb-3 font-mono text-[11px] font-semibold uppercase tracking-[0.14em]">
              {eyebrow}
            </p>
          )}
          <h1 className="font-serif text-3xl font-bold tracking-[-0.03em] text-white lg:text-[40px] lg:leading-none">
            {title}
            {titleAccent && (
              <>
                {' '}
                <span className="text-mint">{titleAccent}</span>
              </>
            )}
          </h1>
          {description && (
            <div className="text-on-forest-soft mt-3 max-w-prose text-sm">{description}</div>
          )}
        </div>
        {actions && (
          <div className="rounded-control text-ink flex flex-wrap items-center gap-2 bg-white p-1.5">
            {actions}
          </div>
        )}
        {tools && <div className="flex flex-wrap items-center gap-2">{tools}</div>}
      </div>
      {feature && <div className="relative mt-6">{feature}</div>}
      {filterTabs ? (
        <div
          role={filterTabsKind === 'tabs' ? 'tablist' : 'group'}
          aria-label={filterTabsLabel}
          className="scrollbar-none relative mt-5 flex max-w-full gap-1 overflow-x-auto"
        >
          {filterTabs.map((tab) => (
            <button
              key={tab.label}
              type="button"
              role={filterTabsKind === 'tabs' ? 'tab' : undefined}
              aria-selected={filterTabsKind === 'tabs' ? tab.active : undefined}
              aria-pressed={filterTabsKind === 'tabs' ? undefined : tab.active}
              onClick={tab.onSelect}
              className={cn(
                'rounded-pill flex h-11 flex-none items-center gap-2 whitespace-nowrap px-4 text-[13px] font-bold md:h-[38px]',
                tab.active
                  ? 'bg-mint text-forest-deep'
                  : 'border border-white/20 text-white hover:bg-white/10',
              )}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span
                  className={cn(
                    'rounded-pill min-w-5 px-1.5 text-center text-[11px] tabular-nums',
                    tab.active ? 'bg-forest-deep/10' : 'bg-white/15',
                  )}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      ) : (
        area.tabs.length > 1 && (
          <div className="relative mt-5">
            <AreaTabs tabs={area.tabs} />
          </div>
        )
      )}
      {below && <div className="relative mt-4">{below}</div>}
    </header>
  );
}
